'use client';

import { getFFmpeg, fetchFile, ffmpegOutputToFile } from './ffmpeg-utils';

/**
 * Generate a video thumbnail using FFmpeg WASM
 * Extracts a frame from the video at the specified time offset
 * 
 * @param videoFile - The video file to generate thumbnail from
 * @param timeOffset - Time offset in seconds (default: 1.0 to avoid black intro frames)
 * @param quality - JPEG quality 1-31, lower is better (default: 5)
 * @returns A File object containing the thumbnail JPEG image
 */
export async function generateVideoThumbnail(
    videoFile: File,
    timeOffset: number = 1.0,
    quality: number = 5
): Promise<File> {
    try {
        const ffmpeg = await getFFmpeg();

        // Generate unique file names
        const inputFileName = `input_${Date.now()}.mp4`;
        const outputFileName = `thumb_${Date.now()}.jpg`;

        // Write input video to FFmpeg filesystem
        await ffmpeg.writeFile(inputFileName, await fetchFile(videoFile));

        // Extract a single frame at the specified time offset
        // -ss: seek to time position
        // -vframes 1: output only 1 frame
        // -q:v: quality level (1-31, lower is better)
        await ffmpeg.exec([
            '-ss', timeOffset.toString(),
            '-i', inputFileName,
            '-vframes', '1',
            '-q:v', quality.toString(),
            '-f', 'image2',
            outputFileName
        ]);

        // Read the generated thumbnail
        const data = await ffmpeg.readFile(outputFileName);

        // Clean up FFmpeg filesystem
        await ffmpeg.deleteFile(inputFileName).catch(() => { });
        await ffmpeg.deleteFile(outputFileName).catch(() => { });

        // Generate output file name based on input
        const baseName = videoFile.name.replace(/\.[^/.]+$/, ''); // Remove extension
        const thumbnailFileName = `${baseName}-thumb.jpg`;

        // Convert to File object
        const thumbnailFile = ffmpegOutputToFile(data, thumbnailFileName, 'image/jpeg');

        return thumbnailFile;
    } catch (error) {
        console.error('[VideoThumbnail] Failed to generate thumbnail:', error);
        throw new Error(`Failed to generate video thumbnail: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Generate thumbnail URL for a video S3 key by convention
 * Thumbnails are stored as {video-key}-thumb.jpg
 * 
 * @param videoKey - The S3 key of the video
 * @returns The expected S3 key for the thumbnail
 */
export function getVideoThumbnailKey(videoKey: string): string {
    // Remove video extension and add -thumb.jpg
    const keyWithoutExtension = videoKey.replace(/\.[^/.]+$/, '');
    return `${keyWithoutExtension}-thumb.jpg`;
}

/**
 * Get the API URL for a video thumbnail
 * 
 * @param videoKey - The S3 key of the video
 * @returns The API URL to fetch the thumbnail
 */
export function getVideoThumbnailUrl(videoKey: string): string {
    const thumbnailKey = getVideoThumbnailKey(videoKey);
    return `/api/media/download?key=${encodeURIComponent(thumbnailKey)}&view=true`;
}

/**
 * Check if a video has a companion thumbnail in S3
 * 
 * @param videoKey - The S3 key of the video
 * @returns Promise<boolean> - true if thumbnail exists
 */
export async function checkVideoThumbnailExists(videoKey: string): Promise<boolean> {
    try {
        const thumbnailUrl = getVideoThumbnailUrl(videoKey);
        const response = await fetch(thumbnailUrl, { method: 'HEAD' });
        return response.ok;
    } catch {
        return false;
    }
}
